<template>
  <div class="p-4">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title
        :title="pageTitle"
        :subtitle="pageSubtitle"
      />
      <div class="flex gap-2">
        <ButtonDaisy
          text="Retour"
          variant="ghost"
          size="sm"
          :icon="ArrowLeftIcon"
          @click="router.back()"
        />
        <ButtonDaisy
          text="Actualiser"
          variant="outline"
          size="sm"
          :icon="ArrowPathIcon"
          :loader="isLoading"
          @click="fetchDerogations"
        />
      </div>
    </div>

    <div class="mt-6 flex flex-nowrap gap-4 overflow-x-auto py-4">
      <CounterCard
        v-for="status in derogationStatusOptions"
        :key="status.value"
        :counter="status"
        :status-counts="derogationStatusCounts"
        :status-type="'derogation'"
        :class="['flex-1 min-w-[100px] shrink-0 self-stretch my-1', { 'ring-2 ring-primary ring-offset-2': selectedStatusFilter === status.value }]"
        @click="onCounterCardClick(status.value)"
      />
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4">
        <div class="flex flex-row flex-wrap items-end gap-2 justify-end">
          <TextInput
            v-model="searchQuery"
            label="Recherche"
            placeholder="N° devis, client"
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[300px]"
          />
          <SelectInput
            v-model="filterDateCreation"
            label="Date création"
            :options="[{ id: 'toutes-creation', value: null, label: 'Toutes' }, ...dateFilterOptions]"
            placeholder="Année/mois"
            class="flex-1 min-w-[150px] max-w-full sm:max-w-[200px]"
          />
          <label v-if="showSubordinatesView" class="flex items-center gap-2 cursor-pointer pb-1 self-end">
            <input
              type="checkbox"
              v-model="includeMyDerogations"
              class="checkbox checkbox-sm checkbox-primary"
            />
            <span class="text-sm">Inclure mes dérogations</span>
          </label>
        </div>
      </div>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="orderDerogationStore.derogations || []"
          :cols="gridColumns"
          :filter-model="gridFilterModel"
          :get-row-height="() => 48"
          :use-server-side-pagination="true"
          :pagination-page-size="pageSize"
          :pagination-total-rows="orderDerogationStore.total || 0"
          :current-page="page"
          :total-pages="orderDerogationStore.totalPages || 1"
          :show-export="false"
          @onFilterChanged="updateFilter"
          @onPaginationChanged="onPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Dérogations</h2>
          </template>
          <template #filter>
            <ColumnSelector :columns="allColumns" cookie-name="derogations-columns-visibility" @columns-changed="onColumnsChanged" />
          </template>
        </GridVue>
      </div>
    </div>

    <DerogationDetailModal
      :derogation="selectedDerogation"
      :is-open="isDetailModalOpen"
      :on-derogation-updated="onDerogationUpdated"
      @close="isDetailModalOpen = false"
      @updated="onDerogationUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import CounterCard from '@/components/common/ui/CounterCard.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import DerogationBadgeGrid from '@/components/data-visualization/grids/DerogationBadgeGrid.vue'
import DerogationActionsCell from '@/components/features/derogations/DerogationActionsCell.vue'
import DerogationDevisLinkCell from '@/components/features/derogations/DerogationDevisLinkCell.vue'
import DerogationDetailModal from '@/components/features/derogations/DerogationDetailModal.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useOrderDerogationStore } from '@/stores/useOrderDerogationStore'
import { formatDate, dateComparator } from '@/utils/dateUtils'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'
import type { FilterModel } from 'ag-grid-community'
import type { ITextFilterParams, IDateFilterParams } from 'ag-grid-community'

const router = useRouter()
const route = useRoute()
const boostCoreStore = useBoostCorePermissionStore()
const usersStore = useBoostCoreUsersStore()
const orderDerogationStore = useOrderDerogationStore()

const isLoading = ref(false)
const page = ref(1)
const pageSize = ref(20)
const statusFilter = ref('')
const filters = ref<Record<string, any>>({})
const searchQuery = ref<string>('')
const selectedStatusFilter = ref<string | null>(null)
const filterDateCreation = ref<string | null>(null)
const selectedDerogation = ref<any>(null)
const isDetailModalOpen = ref(false)
const includeMyDerogations = ref(false)

const visibleColumnFields = ref<string[]>([
  'requester',
  'quoteName',
  'clientName',
  'famille',
  'type',
  'taux',
  'createdAt',
  'status',
  'actions',
])

let timeout: any = null

const isManager = computed(() => boostCoreStore.isManager)
const canValidate = computed(() =>
  boostCoreStore.include('DEROGATION_DV') || boostCoreStore.include('DEROGATION_DG')
)
const showSubordinatesView = computed(() => isManager.value || canValidate.value)

const pageTitle = computed(() => 'Dérogations')
const pageSubtitle = computed(() =>
  showSubordinatesView.value
    ? 'Affiche les dérogations de votre équipe.'
    : 'Affiche vos propres dérogations.'
)

const dateFilterOptions = computed(() => {
  const now = new Date()
  const opts: { id: string; value: string; label: string }[] = []
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    opts.push({ id: val, value: val, label })
  }
  return opts
})

const derogationStatusOptions = computed(() => [
  { value: 'PENDING', label: 'En attente', order: 1 },
  { value: 'APPROVED', label: 'Approuvée', order: 2 },
  { value: 'REJECTED', label: 'Rejetée', order: 3 },
])

const derogationStatusCounts = computed(() => {
  const derogations = orderDerogationStore.statusCounts
  if (!derogations) return {}
  // Build a map of status to count and sumMarge
    const map: Record<string, { count: number; sumMarge: number }> = {
      PENDING: { count: 0, sumMarge: 0 },
      APPROVED: { count: 0, sumMarge: 0 },
      REJECTED: { count: 0, sumMarge: 0 },
    }
    derogationStatusOptions.value.forEach(option => {
        const statusData = derogations[option.value] as number
        if (statusData) {
          map[option.value].count = statusData || 0
        }
    })

    return map

})

const allColumns = computed<ColumnConfig[]>(() => [
  {
    field: 'uuid',
    headerName: '#',
    visible: false,
    locked: true,
  },
  {
    field: 'requester',
    headerName: 'Demandeur',
    visible: true,
  },
  {
    field: 'quoteName',
    headerName: 'Devis',
    visible: true,
  },
  {
    field: 'clientName',
    headerName: 'Client',
    visible: true,
  },
  {
    field: 'famille',
    headerName: 'Famille',
    visible: true,
  },
  {
    field: 'type',
    headerName: 'Type',
    visible: true,
  },
  {
    field: 'taux',
    headerName: 'Taux',
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Date création',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Statut',
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    visible: true,
    locked: true,
  },
])

const gridColumns = computed(() => {
  const visibleFields = new Set(visibleColumnFields.value)
  return [
    {
      field: 'requester',
      headerName: 'Demandeur',
      cellClass: 'border-0',
      hide: !visibleFields.has('requester'),
      valueGetter: (params: any) => {
        const requester = params.data?.requester
        return requester ? [requester.firstName, requester.lastName].filter(Boolean).join(' ') : '-'
      },
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'quoteName',
      headerName: 'Devis',
      cellClass: 'border-0',
      hide: !visibleFields.has('quoteName'),
      cellRenderer: DerogationDevisLinkCell,
      valueGetter: (params: any) => params.data?.order?.quote?.quoteReference || '-',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'clientName',
      headerName: 'Client',
      cellClass: 'border-0',
      hide: !visibleFields.has('clientName'),
      valueGetter: (params: any) => params.data?.order?.quote?.company?.name || '-',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'famille',
      headerName: 'Famille',
      cellClass: 'border-0',
      hide: !visibleFields.has('famille'),
      valueGetter: (params: any) => params.data?.order?.orderTemplate?.family?.label || '-',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'type',
      headerName: 'Type',
      cellClass: 'border-0',
      hide: !visibleFields.has('type'),
      valueFormatter: (params: any) => getDerogationTypeLabel(params.value || ''),
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'taux',
      headerName: 'Taux',
      cellClass: 'border-0',
      filter: false,
      sortable: false,
      hide: !visibleFields.has('taux'),
      valueGetter: (params: any) => {
        const pct = params.data?.order?.quote?.percentMarge
        if (pct == null) return null
        return Number(pct)
      },
      valueFormatter: (params: any) => {
        if (params.value == null) return '-'
        return `${(params.value * 100).toFixed(1)} %`
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date création',
      cellClass: 'border-0',
      filter: 'agDateColumnFilter',
      valueFormatter: (params: any) => formatDate(params.value?.toString() || ''),
      comparator: dateComparator,
      hide: !visibleFields.has('createdAt'),
      filterParams: {
        filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
        maxNumConditions: 1,
      } as IDateFilterParams,
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellClass: 'border-0',
      cellRenderer: DerogationBadgeGrid,
      hide: !visibleFields.has('status'),
      valueGetter: (params: any) => params.data?.status,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      minWidth: 100,
      maxWidth: 100,
      cellRenderer: DerogationActionsCell,
      sortable: false,
      cellClass: 'border-0 grid-action',
      hide: !visibleFields.has('actions'),
    },
  ]
})

const gridFilterModel = computed(() => {
  const f = filters.value
  const model: Record<string, any> = {}
  if (f.status) model.status = f.status
  return Object.keys(model).length ? model : undefined
})

function localYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function buildMonthDateFilter(yearMonth: string | null) {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(yearMonth)) return undefined
  const [y, m] = yearMonth.split('-').map(Number)
  const start = new Date(y, m - 1, 1)
  const end = new Date(y, m, 0)
  return {
    filterType: 'date',
    type: 'inRange',
    dateFrom: localYmd(start),
    dateTo: localYmd(end),
  }
}

function getDerogationTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    'MARGIN': 'Marge faible',
    'PAYMENT_MODE': 'Mode de paiement',
    'MANUAL_SIGNATURE': 'Signature manuelle',
    'EXCEPTIONAL_DISCOUNT': 'Remise exceptionnelle',
    'MISSING_DOCUMENT': 'Document manquant',
    'MARCHE_PUBLIC': 'Marché public',
  }

  return typeLabels[type] || type
}

function onCounterCardClick(statusValue: string) {
  if (selectedStatusFilter.value === statusValue) {
    // Reset status filter
    selectedStatusFilter.value = null
    statusFilter.value = ''
    const { status: _s, ...rest } = filters.value
    filters.value = rest
  } else {
    // Set status filter
    selectedStatusFilter.value = statusValue
    statusFilter.value = statusValue
    filters.value = {
      ...filters.value,
      status: { filterType: 'text', filter: statusValue, type: 'equals' }
    }
  }
  page.value = 1
  fetchDerogations()
}

const fetchDerogations = async () => {
  isLoading.value = true
  try {

    // Handle status filter
    let statusToFilter = undefined
    if (filters.value.status?.filter) {
      statusToFilter = filters.value.status.filter
    } else if (statusFilter.value) {
      statusToFilter = statusFilter.value
    }

    // Construire les options avec tous les filtres
    const options = {
      page: page.value,
      pageSize: pageSize.value,
      status: statusToFilter,
      search: searchQuery.value || undefined,
      createdAt: buildMonthDateFilter(filterDateCreation.value),
      orderBy: 'createdAt',
      order: 'desc',
    }


    if (showSubordinatesView.value) {
      await orderDerogationStore.fetchSubordinatesDerogations({
        ...options,
        includeOwnDerogations: includeMyDerogations.value,
      })
    } else {
      await orderDerogationStore.fetchMyDerogations(options)
    }
  } catch (error) {
    console.error('Error fetching derogations:', error)
  } finally {
    isLoading.value = false
  }
}

const onColumnsChanged = (newVisibleColumns: string[]) => {
  visibleColumnFields.value = newVisibleColumns
}

async function updateFilter(filtersModel: FilterModel) {
  const newFilters: Record<string, any> = {}

  // Handle status filter specifically
  if (filtersModel.status) {
    if (filtersModel.status.filter) {
      newFilters.status = { filterType: 'text', filter: filtersModel.status.filter, type: 'equals' }
      selectedStatusFilter.value = filtersModel.status.filter
    } else if (filtersModel.status.value) {
      newFilters.status = { filterType: 'text', filter: filtersModel.status.value, type: 'equals' }
      selectedStatusFilter.value = filtersModel.status.value
    }
  } else {
    selectedStatusFilter.value = null
  }

  // Copy other filters
  Object.keys(filtersModel).forEach(key => {
    if (key !== 'status' && filtersModel[key]) {
      newFilters[key] = filtersModel[key]
    }
  })

  filters.value = newFilters
  page.value = 1
  await fetchDerogations()
}

const onPaginationChanged = async (newPageSize: number, newPage: number) => {
  if (pageSize.value === newPageSize && page.value === newPage) return
  let changed = false
  if (pageSize.value !== newPageSize) {
    pageSize.value = newPageSize
    changed = true
  }
  if (page.value !== newPage) {
    page.value = newPage
    changed = true
  }

  if (changed) await fetchDerogations()
}

function applyQueryParams() {
  const raw = route.query.status
  const queryStatus = Array.isArray(raw) ? raw[0] : raw
  if (queryStatus && typeof queryStatus === 'string') {
    selectedStatusFilter.value = queryStatus
    statusFilter.value = queryStatus
    filters.value = { ...filters.value, status: { filterType: 'text', filter: queryStatus, type: 'equals' } }
  }
}

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  if (boostCoreStore.user) {

    if (usersStore.users.length === 0) {
        await usersStore.fetchUsers()
    }
  }
  applyQueryParams()
  await fetchDerogations()
})

// Provide the function to child components
provide('onShowDerogationDetail', (derogation: any) => {
  selectedDerogation.value = derogation
  isDetailModalOpen.value = true
})

watch(() => route.query.status, (newStatus) => {
  const statusVal = Array.isArray(newStatus) ? newStatus[0] : newStatus
  if (statusVal) {
    selectedStatusFilter.value = statusVal
    filters.value = { ...filters.value, status: { filterType: 'text', filter: statusVal, type: 'equals' } }
    page.value = 1
    fetchDerogations()
  }
})

watch(searchQuery, () => {
  page.value = 1
  if (timeout) clearTimeout(timeout)
  timeout = setTimeout(() => {
    fetchDerogations()
  }, 500)
}, { immediate: false })

watch(filterDateCreation, () => {
  page.value = 1
  fetchDerogations()
}, { immediate: false })

watch(includeMyDerogations, () => {
  page.value = 1
  fetchDerogations()
})

watch(statusFilter, async () => {
  page.value = 1
  await fetchDerogations()
})

watch(
  filters,
  () => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
      fetchDerogations()
    }, 500)
  },
  { deep: true },
)

const onDerogationUpdated = async () => {
  isDetailModalOpen.value = false
  selectedDerogation.value = null
  await fetchDerogations()
}
</script>
